import { useState, useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";

const Icon = ({ d, size = 18, className = "" }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none"
    stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"
    className={className}>
    <path d={d} />
  </svg>
);

const ICONS = {
  back:     "M19 12H5M12 5l-7 7 7 7",
  box:      "M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16zM3.27 6.96L12 12.01l8.73-5.05M12 22.08V12",
  map:      "M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0zM12 13a3 3 0 1 0 0-6 3 3 0 0 0 0 6z",
  card:     "M1 4h22v16H1zM1 10h22",
  calendar: "M3 4h18v18H3zM16 2v4M8 2v4M3 10h18",
  check:    "M20 6L9 17l-5-5",
  truck:    "M1 3h15v13H1zM16 8h4l3 3v5h-7V8zM5.5 21a1.5 1.5 0 1 0 0-3 1.5 1.5 0 0 0 0 3zM18.5 21a1.5 1.5 0 1 0 0-3 1.5 1.5 0 0 0 0 3z",
  clock:    "M12 22a10 10 0 1 0 0-20 10 10 0 0 0 0 20zM12 6v6l4 2",
  x:        "M18 6L6 18M6 6l12 12",
  receipt:  "M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8zM14 2v6h6M16 13H8M16 17H8M10 9H8",
  return:   "M3 10h10a8 8 0 0 1 8 8v2M3 10l6 6M3 10l6-6",
  package:  "M12 2l9 4.9V17L12 22 3 17V6.9L12 2zM12 22V12M3 7l9 5 9-5",
  refund:   "M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z",
  done:     "M22 11.08V12a10 10 0 1 1-5.93-9.14M22 4L12 14.01l-3-3",
  cancel:   "M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0zM12 9v4M12 17h.01",
  alert:    "M12 9v4M12 17h.01M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z",
};

const FLOW_STATUSES = [
  { key: "placed",           label: "Placed",           icon: ICONS.receipt },
  { key: "processing",       label: "Processing",       icon: ICONS.clock   },
  { key: "shipped",          label: "Shipped",          icon: ICONS.box     },
  { key: "out_for_delivery", label: "Out for Delivery", icon: ICONS.truck   },
  { key: "delivered",        label: "Delivered",        icon: ICONS.check   },
];

const RETURN_FLOW = [
  { key: "requested",        label: "Requested",        icon: ICONS.return  },
  { key: "approved",         label: "Approved",         icon: ICONS.check   },
  { key: "pickup_scheduled", label: "Pickup Scheduled", icon: ICONS.calendar},
  { key: "picked_up",        label: "Picked Up",        icon: ICONS.package },
  { key: "refund_initiated", label: "Refund Initiated", icon: ICONS.refund  },
  { key: "completed",        label: "Completed",        icon: ICONS.done    },
];

const TERMINAL_BAD = ["cancelled"];

const STATUS_META = {
  placed:           { color: "text-blue-600",    bg: "bg-blue-50",    dot: "bg-blue-500",    bar: "bg-blue-500"    },
  processing:       { color: "text-amber-600",   bg: "bg-amber-50",   dot: "bg-amber-500",   bar: "bg-amber-500"   },
  shipped:          { color: "text-violet-600",  bg: "bg-violet-50",  dot: "bg-violet-500",  bar: "bg-violet-500"  },
  out_for_delivery: { color: "text-orange-600",  bg: "bg-orange-50",  dot: "bg-orange-500",  bar: "bg-orange-500"  },
  delivered:        { color: "text-emerald-600", bg: "bg-emerald-50", dot: "bg-emerald-500", bar: "bg-emerald-500" },
  cancelled:        { color: "text-red-600",     bg: "bg-red-50",     dot: "bg-red-400",     bar: "bg-red-400"     },
};

const RETURN_META = {
  requested:        { color: "text-amber-600",   bg: "bg-amber-50",   dot: "bg-amber-500",   bar: "bg-amber-500"   },
  approved:         { color: "text-blue-600",    bg: "bg-blue-50",    dot: "bg-blue-500",    bar: "bg-blue-500"    },
  pickup_scheduled: { color: "text-violet-600",  bg: "bg-violet-50",  dot: "bg-violet-500",  bar: "bg-violet-500"  },
  picked_up:        { color: "text-orange-600",  bg: "bg-orange-50",  dot: "bg-orange-500",  bar: "bg-orange-500"  },
  refund_initiated: { color: "text-rose-600",    bg: "bg-rose-50",    dot: "bg-rose-500",    bar: "bg-rose-500"    },
  completed:        { color: "text-emerald-600", bg: "bg-emerald-50", dot: "bg-emerald-500", bar: "bg-emerald-500" },
  rejected:         { color: "text-red-600",     bg: "bg-red-50",     dot: "bg-red-400",     bar: "bg-red-400"     },
};

function fmtDate(iso) {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString("en-US", { day: "numeric", month: "short", year: "numeric" });
}
function fmtTime(iso) {
  if (!iso) return "";
  return new Date(iso).toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit" });
}

function mapStatus(dbStatus) {
  const map = {
    pending:    "placed",
    confirmed:  "placed",
    processing: "processing",
    shipped:    "shipped",
    delivered:  "delivered",
    cancelled:  "cancelled",
  };
  return map[dbStatus] || "placed";
}

function getExpectedDelivery(createdAt) {
  if (!createdAt) return null;
  const d = new Date(createdAt);
  d.setDate(d.getDate() + 7);
  return d.toISOString();
}

// ── Cancel Confirmation Modal ─────────────────
function CancelModal({ order, onConfirm, onClose, loading }) {
  const hasStripe = !!order.stripe_payment_id;
  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center px-4 pb-4 sm:pb-0"
      style={{ background: "rgba(0,0,0,0.45)" }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm p-6 space-y-4 animate-[slideUp_0.2s_ease-out]">
        {/* Icon */}
        <div className="w-12 h-12 rounded-2xl bg-red-50 flex items-center justify-center mx-auto">
          <Icon d={ICONS.alert} size={22} className="text-red-500" />
        </div>

        {/* Text */}
        <div className="text-center space-y-1.5">
          <h3 className="text-base font-bold text-stone-900">Cancel this order?</h3>
          <p className="text-sm text-stone-500 leading-relaxed">
            Order <span className="font-mono font-bold text-stone-800">#{order.id}</span> will be permanently cancelled.
          </p>
        </div>

        {/* Refund notice */}
        {hasStripe && (
          <div className="flex items-start gap-2.5 bg-emerald-50 rounded-xl px-4 py-3">
            <span className="text-lg mt-0.5">💳</span>
            <div>
              <p className="text-xs font-bold text-emerald-700">Refund will be initiated</p>
              <p className="text-xs text-emerald-600 mt-0.5 leading-relaxed">
                Your full payment of <span className="font-bold">${Number(order.total).toLocaleString()}</span> will be refunded to your original payment method within 5–7 business days.
              </p>
            </div>
          </div>
        )}

        {/* Reward points notice */}
        {(parseInt(order.reward_points_earned) > 0 || parseInt(order.reward_points_used) > 0) && (
          <div className="flex items-start gap-2.5 bg-amber-50 rounded-xl px-4 py-3">
            <span className="text-lg mt-0.5">🪙</span>
            <div className="text-xs text-amber-700 space-y-0.5">
              {parseInt(order.reward_points_earned) > 0 && (
                <p><span className="font-bold">{order.reward_points_earned} pts</span> earned on this order will be removed.</p>
              )}
              {parseInt(order.reward_points_used) > 0 && (
                <p><span className="font-bold">{order.reward_points_used} pts</span> you redeemed will be restored.</p>
              )}
            </div>
          </div>
        )}

        {/* Actions */}
        <div className="flex gap-2 pt-1">
          <button onClick={onClose} disabled={loading}
            className="flex-1 py-2.5 rounded-xl border border-stone-200 text-sm font-semibold text-stone-600 hover:bg-stone-50 transition disabled:opacity-60">
            Keep Order
          </button>
          <button onClick={onConfirm} disabled={loading}
            className="flex-1 py-2.5 rounded-xl bg-red-500 text-white text-sm font-bold hover:bg-red-600 transition disabled:opacity-60 flex items-center justify-center gap-1.5">
            {loading
              ? <><span className="w-3.5 h-3.5 border-2 border-white/40 border-t-white rounded-full animate-spin" /> Cancelling…</>
              : "Yes, Cancel"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Order Progress Bar ────────────────────────────────────────
function StatusProgress({ status }) {
  const isBad     = TERMINAL_BAD.includes(status);
  const activeIdx = FLOW_STATUSES.findIndex(s => s.key === status);
  const meta      = STATUS_META[status] || STATUS_META.placed;

  if (isBad) {
    return (
      <div className={`rounded-2xl px-5 py-4 ${meta.bg} flex items-center gap-3`}>
        <span className={`w-2.5 h-2.5 rounded-full ${meta.dot} flex-shrink-0`} />
        <div>
          <p className={`font-bold text-sm capitalize ${meta.color}`}>Order {status}</p>
          <p className="text-xs text-stone-400 mt-0.5">This order has been {status}. Contact support if you need help.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-start justify-between relative">
        <div className="absolute top-4 left-0 right-0 h-px bg-stone-100 mx-8 z-0" />
        {FLOW_STATUSES.map((step, idx) => {
          const done     = idx < activeIdx;
          const current  = idx === activeIdx;
          const stepMeta = STATUS_META[step.key];
          return (
            <div key={step.key} className="flex flex-col items-center gap-1.5 z-10 flex-1">
              <div className={`w-8 h-8 rounded-full flex items-center justify-center border-2 transition-all duration-500
                ${done    ? `${stepMeta.bg} border-transparent` : ""}
                ${current ? `${stepMeta.bg} border-current ${stepMeta.color} ring-4 ring-offset-2 ring-stone-100` : ""}
                ${!done && !current ? "bg-white border-stone-200 text-stone-300" : ""}`}>
                {done
                  ? <Icon d={ICONS.check} size={14} className="text-emerald-500" />
                  : <Icon d={step.icon} size={13} className={current ? stepMeta.color : "text-stone-300"} />}
              </div>
              <span className={`text-[10px] font-semibold text-center leading-tight
                ${current ? stepMeta.color : done ? "text-stone-500" : "text-stone-300"}`}>
                {step.label}
              </span>
            </div>
          );
        })}
      </div>
      <div className="h-1.5 bg-stone-100 rounded-full overflow-hidden">
        <div className={`h-full rounded-full transition-all duration-700 ease-out ${meta.bar}`}
          style={{ width: `${(activeIdx / (FLOW_STATUSES.length - 1)) * 100}%` }} />
      </div>
    </div>
  );
}

// ── Return Progress Tracker ───────────────────────────────────
function ReturnProgress({ returnStatus, pickupDate, returnNotes }) {
  if (returnStatus === "rejected") {
    return (
      <div className="rounded-2xl px-5 py-4 bg-red-50 flex items-start gap-3">
        <span className="w-2.5 h-2.5 rounded-full bg-red-400 flex-shrink-0 mt-1" />
        <div>
          <p className="font-bold text-sm text-red-600">Return Rejected</p>
          <p className="text-xs text-stone-400 mt-0.5">
            {returnNotes || "Your return request was not approved. Contact support for more info."}
          </p>
        </div>
      </div>
    );
  }

  const activeIdx = RETURN_FLOW.findIndex(s => s.key === returnStatus);

  return (
    <div className="space-y-4">
      <div className="flex items-start justify-between relative">
        <div className="absolute top-4 left-0 right-0 h-px bg-stone-100 mx-4 z-0" />
        {RETURN_FLOW.map((step, idx) => {
          const done    = idx < activeIdx;
          const current = idx === activeIdx;
          const meta    = RETURN_META[step.key];
          return (
            <div key={step.key} className="flex flex-col items-center gap-1.5 z-10 flex-1">
              <div className={`w-8 h-8 rounded-full flex items-center justify-center border-2 transition-all duration-500
                ${done    ? "bg-emerald-50 border-transparent" : ""}
                ${current ? `${meta.bg} border-current ${meta.color} ring-4 ring-offset-2 ring-stone-100` : ""}
                ${!done && !current ? "bg-white border-stone-200 text-stone-300" : ""}`}>
                {done
                  ? <Icon d={ICONS.check} size={14} className="text-emerald-500" />
                  : <Icon d={step.icon} size={13} className={current ? meta.color : "text-stone-300"} />}
              </div>
              <span className={`text-[10px] font-semibold text-center leading-tight
                ${current ? meta.color : done ? "text-stone-500" : "text-stone-300"}`}>
                {step.label}
              </span>
            </div>
          );
        })}
      </div>
      <div className="h-1.5 bg-stone-100 rounded-full overflow-hidden">
        <div className={`h-full rounded-full transition-all duration-700 ease-out ${RETURN_META[returnStatus]?.bar || "bg-amber-500"}`}
          style={{ width: `${(activeIdx / (RETURN_FLOW.length - 1)) * 100}%` }} />
      </div>

      {pickupDate && (
        <div className="flex items-center gap-2 text-xs text-stone-500 bg-violet-50 rounded-xl px-3 py-2.5">
          <Icon d={ICONS.calendar} size={13} className="text-violet-400" />
          <span>Pickup scheduled for <span className="font-bold text-stone-800">{fmtDate(pickupDate)}</span></span>
        </div>
      )}

      {returnNotes && (
        <div className="flex items-start gap-2 text-xs text-stone-500 bg-stone-50 rounded-xl px-3 py-2.5">
          <span className="text-stone-400 mt-0.5">📋</span>
          <span>{returnNotes}</span>
        </div>
      )}

      {returnStatus === "refund_initiated" && (
        <div className="flex items-center gap-2 text-xs text-rose-600 bg-rose-50 rounded-xl px-3 py-2.5 font-semibold">
          <span>💳</span>
          <span>Refund has been initiated — expect it in 5–7 business days</span>
        </div>
      )}
      {returnStatus === "completed" && (
        <div className="flex items-center gap-2 text-xs text-emerald-600 bg-emerald-50 rounded-xl px-3 py-2.5 font-semibold">
          <span>✅</span>
          <span>Return completed and refund processed successfully</span>
        </div>
      )}
    </div>
  );
}

// ── Return Request Form ───────────────────────────────────────
function ReturnRequestForm({ orderId, onSubmitted }) {
  const [reason,     setReason]     = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error,      setError]      = useState("");

  const API = process.env.REACT_APP_API_URL || "http://localhost:5001";
  async function handleSubmit() {
    if (!reason.trim()) { setError("Please provide a reason."); return; }
    setSubmitting(true); setError("");
    try {
      const token = localStorage.getItem("sf_token");
      const res = await fetch(`${API}/api/orders/${orderId}/return`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ reason: reason.trim() }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message);
      onSubmitted(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="mt-4 space-y-3">
      {error && <p className="text-xs text-red-500 bg-red-50 rounded-xl px-3 py-2">{error}</p>}
      <textarea
        value={reason}
        onChange={e => setReason(e.target.value)}
        rows={3}
        placeholder="Tell us why you want to return this order (e.g. wrong size, damaged item, not as described)..."
        className="w-full px-4 py-3 rounded-xl border border-stone-200 text-sm text-stone-800 placeholder-stone-400 focus:outline-none focus:ring-2 focus:ring-rose-400 resize-none"
      />
      <div className="flex gap-2">
        <button onClick={handleSubmit} disabled={submitting}
          className="flex-1 py-2.5 rounded-xl bg-rose-500 text-white text-sm font-bold hover:bg-rose-600 transition disabled:opacity-60">
          {submitting ? "Submitting..." : "Submit Return Request"}
        </button>
      </div>
      <p className="text-[11px] text-stone-400 text-center">
        Returns accepted within 7 days of delivery · Items must be in original condition
      </p>
    </div>
  );
}

// ── Main Component ────────────────────────────────────────────
export default function OrderDetail() {
  const { id }    = useParams();
  const navigate  = useNavigate();
  const [order,          setOrder]          = useState(null);
  const [loading,        setLoading]        = useState(true);
  const [error,          setError]          = useState(null);
  const [showReturnForm, setShowReturnForm] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [cancelling,      setCancelling]     = useState(false);
  const [cancelError,     setCancelError]    = useState("");
  const [refundMsg,       setRefundMsg]      = useState("");

  const API = process.env.REACT_APP_API_URL || "http://localhost:5001";
  useEffect(() => {
    setLoading(true);
    const token = localStorage.getItem("sf_token");
    fetch(`${API}/api/orders/${id}`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.json())
      .then(data => {
        const mapped = {
          id:                   data.id,
          rawStatus:            data.status,
          status:               mapStatus(data.status),
          status_updated_at:    data.updated_at,
          placed_at:            data.created_at,
          expected_delivery:    getExpectedDelivery(data.created_at),
          return_status:        data.return_status || "none",
          return_reason:        data.return_reason || "",
          return_requested_at:  data.return_requested_at,
          return_pickup_date:   data.return_pickup_date,
          return_notes:         data.return_notes,
          stripe_payment_id:    data.stripe_payment_id || null,
          reward_points_earned: data.reward_points_earned || 0,
          reward_points_used:   data.reward_points_used   || 0,
          refund_initiated_at:  data.refund_initiated_at  || null,
          items: (data.items || []).map(item => ({
            id:    item.id,
            name:  item.name,
            qty:   item.qty,
            price: item.price,
            img:   item.image || "",
          })),
          address: {
            name:  data.full_name      || "",
            line1: data.address_line1  || "",
            line2: data.address_line2  || "",
            city:  data.city           || "",
            pin:   data.zip            || "",
            phone: data.phone          || "",
          },
          payment: {
            method:   data.payment_method || "Card",
            last4:    data.stripe_payment_id ? data.stripe_payment_id.slice(-4) : null,
            subtotal: Number(data.subtotal),
            shipping: Number(data.delivery_charge || 0),
            total:    Number(data.total),
          },
        };
        setOrder(mapped);
        setLoading(false);
      })
      .catch(() => { setError("Could not load order."); setLoading(false); });
  }, [id]);

  async function handleConfirmCancel() {
    setCancelling(true);
    setCancelError("");
    try {
      const token = localStorage.getItem("sf_token");
      const res = await fetch(`${API}/api/orders/${id}/cancel`, {
        method: "PATCH",
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message);

      setOrder(prev => ({ ...prev, rawStatus: "cancelled", status: "cancelled" }));
      setShowCancelModal(false);
      setRefundMsg(data.message || "Order cancelled successfully.");
    } catch (err) {
      setCancelError(err.message || "Failed to cancel. Please try again.");
    } finally {
      setCancelling(false);
    }
  }

  if (loading) return (
    <div className="min-h-screen bg-stone-50 flex items-center justify-center">
      <div className="flex flex-col items-center gap-3 text-stone-400">
        <div className="w-8 h-8 border-2 border-stone-200 border-t-stone-600 rounded-full animate-spin" />
        <p className="text-sm">Loading order…</p>
      </div>
    </div>
  );

  if (error) return (
    <div className="min-h-screen bg-stone-50 flex items-center justify-center px-4">
      <div className="text-center space-y-3">
        <p className="text-stone-500 text-sm">{error}</p>
        <button onClick={() => navigate(-1)} className="text-xs text-rose-500 hover:underline">← Go back</button>
      </div>
    </div>
  );

  const { status, items, address, payment, placed_at, expected_delivery,
          status_updated_at, return_status, return_reason, return_requested_at,
          return_pickup_date, return_notes } = order;

  const meta    = STATUS_META[status] || STATUS_META.placed;
  const isBad   = TERMINAL_BAD.includes(status);
  const subtotal = payment.subtotal;
  const total    = payment.total;

  // Can customer cancel?
  const canCancel = ["pending", "confirmed"].includes(order.rawStatus);

  // Can customer request return?
  const canRequestReturn = order.rawStatus === "delivered" && return_status === "none";
  const hasActiveReturn  = return_status !== "none";

  const deliveredAt  = new Date(status_updated_at);
  const daysSince    = (new Date() - deliveredAt) / (1000 * 60 * 60 * 24);
  const withinWindow = daysSince <= 7;

  return (
    <div className="min-h-screen bg-stone-50">
      {/* Cancel confirmation modal */}
      {showCancelModal && (
        <CancelModal
          order={{ ...order, total: payment.total }}
          onConfirm={handleConfirmCancel}
          onClose={() => { setShowCancelModal(false); setCancelError(""); }}
          loading={cancelling}
        />
      )}

      {/* Top bar */}
      <div className="bg-white border-b border-stone-100 sticky top-0 z-10">
        <div className="max-w-2xl mx-auto px-4 h-14 flex items-center gap-3">
          <button onClick={() => navigate(-1)}
            className="w-8 h-8 rounded-xl bg-stone-100 flex items-center justify-center text-stone-600 hover:bg-stone-200 transition">
            <Icon d={ICONS.back} size={15} />
          </button>
          <div className="flex-1 min-w-0">
            <h1 className="text-sm font-bold text-stone-900 font-mono truncate">#{order.id}</h1>
            <p className="text-[11px] text-stone-400">Placed {fmtDate(placed_at)}</p>
          </div>
          <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold ${meta.bg} ${meta.color}`}>
            <span className={`w-1.5 h-1.5 rounded-full ${meta.dot}`} />
            {status.replace(/_/g, " ").replace(/\b\w/g, c => c.toUpperCase())}
          </span>
        </div>
      </div>

      <div className="max-w-2xl mx-auto px-4 py-6 space-y-4">

        {/* Refund success message */}
        {refundMsg && (
          <div className="flex items-start gap-3 bg-emerald-50 border border-emerald-100 rounded-2xl px-4 py-3.5">
            <span className="text-lg mt-0.5">💳</span>
            <div>
              <p className="text-sm font-bold text-emerald-700">Order Cancelled</p>
              <p className="text-xs text-emerald-600 mt-0.5">{refundMsg}</p>
            </div>
          </div>
        )}

        {/* Cancel error */}
        {cancelError && (
          <div className="flex items-center gap-2 bg-red-50 rounded-xl px-4 py-3 text-xs text-red-600">
            <Icon d={ICONS.x} size={13} className="text-red-400 shrink-0" />
            {cancelError}
          </div>
        )}

        {/* Order Status Tracker */}
        <div className="bg-white rounded-2xl border border-stone-100 shadow-sm p-5">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xs font-bold uppercase tracking-widest text-stone-400">Order Status</h2>
            {status_updated_at && (
              <span className="text-[10px] text-stone-300">
                Updated {fmtDate(status_updated_at)} {fmtTime(status_updated_at)}
              </span>
            )}
          </div>
          <StatusProgress status={status} />
          {!isBad && expected_delivery && order.rawStatus !== "delivered" && (
            <div className="mt-4 flex items-center gap-2 text-xs text-stone-500 bg-stone-50 rounded-xl px-3 py-2.5">
              <Icon d={ICONS.calendar} size={13} className="text-stone-400" />
              <span>Expected delivery by <span className="font-bold text-stone-800">{fmtDate(expected_delivery)}</span></span>
            </div>
          )}

          {/* Refund info for cancelled orders */}
          {status === "cancelled" && order.refund_initiated_at && (
            <div className="mt-3 flex items-center gap-2 text-xs text-emerald-600 bg-emerald-50 rounded-xl px-3 py-2.5 font-semibold">
              <span>💳</span>
              <span>Refund initiated on {fmtDate(order.refund_initiated_at)} — expect it in 5–7 business days</span>
            </div>
          )}

          {/* Cancel button */}
          {canCancel && (
            <button
              onClick={() => { setCancelError(""); setShowCancelModal(true); }}
              className="mt-4 w-full py-2.5 rounded-xl border-2 border-red-200 text-red-500 text-sm font-bold hover:bg-red-50 hover:border-red-300 transition flex items-center justify-center gap-2">
              <Icon d={ICONS.x} size={15} />
              Cancel Order
            </button>
          )}
        </div>

        {/* Return Section */}
        {(hasActiveReturn || canRequestReturn) && (
          <div className="bg-white rounded-2xl border border-stone-100 shadow-sm p-5">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xs font-bold uppercase tracking-widest text-stone-400">Return Status</h2>
              {return_requested_at && (
                <span className="text-[10px] text-stone-300">Requested {fmtDate(return_requested_at)}</span>
              )}
            </div>

            {hasActiveReturn && (
              <>
                <ReturnProgress
                  returnStatus={return_status}
                  pickupDate={return_pickup_date}
                  returnNotes={return_notes}
                />
                {return_reason && (
                  <div className="mt-4 bg-stone-50 rounded-xl px-4 py-3">
                    <p className="text-xs font-semibold text-stone-400 uppercase tracking-widest mb-1">Your Reason</p>
                    <p className="text-sm text-stone-600">{return_reason}</p>
                  </div>
                )}
              </>
            )}

            {canRequestReturn && withinWindow && !showReturnForm && (
              <div className="space-y-3">
                <div className="flex items-center gap-2 bg-amber-50 rounded-xl px-4 py-3">
                  <span className="text-lg">📦</span>
                  <div>
                    <p className="text-sm font-semibold text-stone-800">Want to return this order?</p>
                    <p className="text-xs text-stone-400">Return window closes {fmtDate(new Date(new Date(status_updated_at).getTime() + 7 * 24 * 60 * 60 * 1000).toISOString())}</p>
                  </div>
                </div>
                <button
                  onClick={() => setShowReturnForm(true)}
                  className="w-full py-3 rounded-xl border-2 border-rose-200 text-rose-600 text-sm font-bold hover:bg-rose-50 transition flex items-center justify-center gap-2">
                  <Icon d={ICONS.return} size={16} />
                  Request Return
                </button>
              </div>
            )}

            {canRequestReturn && !withinWindow && (
              <div className="flex items-center gap-2 bg-stone-50 rounded-xl px-4 py-3">
                <span className="text-lg">⏰</span>
                <p className="text-sm text-stone-500">Return window has expired (7 days after delivery)</p>
              </div>
            )}

            {showReturnForm && (
              <ReturnRequestForm
                orderId={order.id}
                onSubmitted={(updated) => {
                  setOrder(prev => ({
                    ...prev,
                    return_status:       updated.return_status,
                    return_reason:       updated.return_reason,
                    return_requested_at: updated.return_requested_at,
                  }));
                  setShowReturnForm(false);
                }}
              />
            )}
          </div>
        )}

        {/* Items */}
        <div className="bg-white rounded-2xl border border-stone-100 shadow-sm">
          <div className="px-5 pt-5 pb-1">
            <h2 className="text-xs font-bold uppercase tracking-widest text-stone-400">Items · {items.length}</h2>
          </div>
          <div className="divide-y divide-stone-50 px-5">
            {items.map(item => (
              <div key={item.id} className="flex items-center gap-4 py-4">
                {item.img ? (
                  <img src={item.img} alt={item.name}
                    className="w-16 h-16 rounded-xl object-cover bg-stone-100 flex-shrink-0 border border-stone-100" />
                ) : (
                  <div className="w-16 h-16 rounded-xl bg-stone-100 flex-shrink-0 flex items-center justify-center border border-stone-100">
                    <span className="text-xs text-stone-400 text-center px-1 leading-tight">{item.name.split(" ").slice(0,2).join(" ")}</span>
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-stone-800 truncate">{item.name}</p>
                  <p className="text-xs text-stone-400 mt-0.5">Qty: {item.qty}</p>
                </div>
                <p className="text-sm font-bold text-stone-900 flex-shrink-0">
                  ${(item.price * item.qty).toLocaleString()}
                </p>
              </div>
            ))}
          </div>
        </div>

        {/* Address + Payment */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="bg-white rounded-2xl border border-stone-100 shadow-sm p-5">
            <h2 className="text-xs font-bold uppercase tracking-widest text-stone-400 mb-3">Delivery Address</h2>
            <div className="flex gap-3">
              <div className="w-8 h-8 rounded-xl bg-stone-100 flex items-center justify-center flex-shrink-0">
                <Icon d={ICONS.map} size={14} className="text-stone-500" />
              </div>
              <div>
                <p className="text-sm font-semibold text-stone-800">{address.name}</p>
                <p className="text-xs text-stone-500 mt-1 leading-relaxed">
                  {address.line1}<br />
                  {address.line2 && <>{address.line2}<br /></>}
                  {address.city}<br />
                  {address.pin}
                </p>
                <p className="text-xs text-stone-400 mt-1">{address.phone}</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-2xl border border-stone-100 shadow-sm p-5">
            <h2 className="text-xs font-bold uppercase tracking-widest text-stone-400 mb-3">Payment</h2>
            <div className="flex gap-3 mb-4">
              <div className="w-8 h-8 rounded-xl bg-stone-100 flex items-center justify-center flex-shrink-0">
                <Icon d={ICONS.card} size={14} className="text-stone-500" />
              </div>
              <div>
                <p className="text-sm font-semibold text-stone-800">{payment.method}</p>
                {payment.last4 && <p className="text-xs text-stone-400 mt-0.5">•••• {payment.last4}</p>}
              </div>
            </div>
            <div className="space-y-1.5 border-t border-stone-50 pt-3">
              <div className="flex justify-between text-xs text-stone-500">
                <span>Subtotal</span><span>${subtotal.toLocaleString()}</span>
              </div>
              <div className="flex justify-between text-xs text-stone-500">
                <span>Shipping</span>
                <span className="text-emerald-600 font-medium">
                  {payment.shipping === 0 ? "Free" : `$${payment.shipping}`}
                </span>
              </div>
              <div className="flex justify-between text-sm font-bold text-stone-900 pt-1 border-t border-stone-100">
                <span>Total</span><span>${total.toLocaleString()}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Support nudge */}
        <div className="flex items-center justify-center gap-1.5 py-2">
          <p className="text-xs text-stone-400">Issue with this order?</p>
          <Link to="/settings" state={{ tab: "support" }}
            className="text-xs font-semibold text-rose-500 hover:underline">
            Contact Support
          </Link>
        </div>

      </div>
    </div>
  );
}